import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Search, Bot, Send, Building2, FileText, Users, Loader2, AlertCircle, CheckCircle, ArrowLeft, Briefcase, MousePointer } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

const ENTITY_ICONS = {
  application: { icon: Briefcase, color: 'text-purple-600', bg: 'bg-purple-100', label: 'KYB Application' },
  company: { icon: Building2, color: 'text-blue-600', bg: 'bg-blue-100', label: 'Company' },
  request: { icon: FileText, color: 'text-green-600', bg: 'bg-green-100', label: 'Verification Request' },
  client: { icon: Users, color: 'text-orange-600', bg: 'bg-orange-100', label: 'Client File' }
};

// currentContext: { type: 'request'|'client'|'company'|'application', id: string, name: string }
export default function AIRiskManagerModal({ isOpen, onClose, currentContext }) {
  const [step, setStep] = useState('choose'); // choose, search, confirm, chat
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [entitySummary, setEntitySummary] = useState('');
  const messagesEndRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Real-time search as user types
  const performSearch = useCallback(async (term) => {
    if (!term || term.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const res = await api.get(`/ai-risk-manager/search?type=all&q=${encodeURIComponent(term)}`);
      setSearchResults(res.data.results || []);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearching(false);
    }
  }, []);

  // Debounced search on input change
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      performSearch(searchTerm);
    }, 300);
    return () => clearTimeout(searchTimeoutRef.current);
  }, [searchTerm, performSearch]);

  const resetModal = () => {
    // If there's a current context, start at 'choose', otherwise go straight to 'search'
    setStep(currentContext ? 'choose' : 'search');
    setSearchTerm('');
    setSearchResults([]);
    setSelectedEntity(null);
    setConversationId(null);
    setMessages([]);
    setInputMessage('');
    setEntitySummary('');
  };

  // When modal opens, set the initial step based on context
  useEffect(() => {
    if (isOpen) {
      setStep(currentContext ? 'choose' : 'search');
    }
  }, [isOpen, currentContext]);

  const [starting, setStarting] = useState(false);

  const startConversationWithEntity = async (entity) => {
    setSelectedEntity(entity);
    setStarting(true);
    try {
      const res = await api.post('/ai-risk-manager/conversations', {
        entityType: entity.type,
        entityId: entity.id
      });
      setConversationId(res.data.conversationId);
      setEntitySummary(res.data.entitySummary);
      setMessages([{
        role: 'assistant',
        content: `Hello! I'm your AI Compliance Assistant. I've reviewed the anonymised data for this ${entity.type}.\n\nHow can I help you today?\n\nYou can ask me things like:\n• What are the main risks or concerns?\n• Is there anything I should be worried about?\n• What's the overall compliance status?\n• What should be the next steps?\n• Explain any red flags you see.`
      }]);
      setStep('chat');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to start conversation');
    } finally {
      setStarting(false);
    }
  };

  const handleSelectCurrentRecord = async () => {
    if (!currentContext) return;
    const entity = {
      type: currentContext.type,
      id: currentContext.id,
      displayName: currentContext.name || 'Current Record'
    };
    await startConversationWithEntity(entity);
  };

  const handleClose = () => {
    if (conversationId) {
      api.delete(`/ai-risk-manager/conversations/${conversationId}`).catch(() => {});
    }
    resetModal();
    onClose();
  };

  const handleSelectEntity = async (entity) => {
    await startConversationWithEntity(entity);
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || sending) return;
    const question = inputMessage.trim();
    setInputMessage('');
    setMessages(prev => [...prev, { role: 'user', content: question }]);
    setSending(true);
    
    try {
      const res = await api.post(`/ai-risk-manager/conversations/${conversationId}/ask`, { question });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (error) {
      toast.error('Failed to get response');
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }]);
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {step !== 'choose' && step !== 'search' && (
              <button onClick={() => {
                if (step === 'chat') setStep('confirm');
                else if (step === 'confirm') setStep(currentContext ? 'choose' : 'search');
                else setStep(currentContext ? 'choose' : 'search');
              }}
                className="p-1 hover:bg-gray-100 rounded">
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <Bot className="w-6 h-6 text-purple-600" />
            <div>
              <h2 className="font-semibold text-gray-900">AI Risk Manager</h2>
              <p className="text-xs text-gray-500">Analyse entities with AI assistance</p>
            </div>
          </div>
          <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Loading state */}
          {starting && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-purple-600 mb-4" />
              <p className="text-gray-600">Loading record data...</p>
            </div>
          )}

          {/* Choose step - This Record or Search */}
          {step === 'choose' && currentContext && !starting && (
            <div className="space-y-4">
              <p className="text-gray-600">What would you like to analyse?</p>

              {/* This Record Button */}
              <button
                onClick={handleSelectCurrentRecord}
                disabled={starting}
                className="w-full p-4 border-2 border-purple-200 rounded-xl hover:bg-purple-50 hover:border-purple-400 text-left flex items-center gap-4 transition-all disabled:opacity-50"
              >
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MousePointer className="w-6 h-6 text-purple-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">This Record</p>
                  <p className="text-sm text-gray-500">Analyse: {currentContext.name || 'Current page record'}</p>
                  <span className="text-xs text-purple-600 mt-1 inline-block">
                    {ENTITY_ICONS[currentContext.type]?.label || currentContext.type}
                  </span>
                </div>
              </button>

              {/* Search Button */}
              <button
                onClick={() => setStep('search')}
                disabled={starting}
                className="w-full p-4 border-2 border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-400 text-left flex items-center gap-4 transition-all disabled:opacity-50"
              >
                <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Search className="w-6 h-6 text-gray-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">Search</p>
                  <p className="text-sm text-gray-500">Find a different record to analyse</p>
                </div>
              </button>
            </div>
          )}

          {step === 'search' && (
            <div className="space-y-4">
              {currentContext && (
                <button
                  onClick={() => setStep('choose')}
                  className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to options
                </button>
              )}
              <p className="text-gray-600">Search for any application, company, request, or client to analyse</p>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Start typing to search..."
                  autoFocus
                  className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500" />
                {searching && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" />
                )}
              </div>
              {searchResults.length > 0 && (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {searchResults.map(result => {
                    const entityInfo = ENTITY_ICONS[result.type] || ENTITY_ICONS.application;
                    const Icon = entityInfo.icon;
                    return (
                      <button key={`${result.type}-${result.id}`} onClick={() => handleSelectEntity(result)}
                        className="w-full p-3 border rounded-lg hover:bg-gray-50 text-left flex items-center gap-3 transition-colors">
                        <div className={`w-10 h-10 ${entityInfo.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                          <Icon className={`w-5 h-5 ${entityInfo.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{result.displayName}</p>
                          <p className="text-xs text-gray-500 truncate">{result.subtext}</p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${entityInfo.bg} ${entityInfo.color} flex-shrink-0`}>
                          {entityInfo.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
              {searchResults.length === 0 && searchTerm.length >= 2 && !searching && (
                <p className="text-gray-500 text-center py-4">No results found</p>
              )}
              {searchTerm.length < 2 && (
                <div className="text-center py-8 text-gray-400">
                  <Search className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Type at least 2 characters to search</p>
                </div>
              )}
            </div>
          )}



          {step === 'chat' && (
            <div className="space-y-4">
              <div className="bg-purple-50 px-3 py-2 rounded-lg text-sm text-purple-700 flex items-center gap-2">
                <Bot className="w-4 h-4" />
                <span>Reviewing: {entitySummary}</span>
              </div>
              <div className="space-y-3 min-h-[300px] max-h-[400px] overflow-y-auto">
                {messages.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] p-3 rounded-lg ${msg.role === 'user' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-900'}`}>
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  </div>
                ))}
                {sending && (
                  <div className="flex justify-start">
                    <div className="bg-gray-100 p-3 rounded-lg">
                      <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </div>
          )}
        </div>

        {/* Chat Input */}
        {step === 'chat' && (
          <div className="p-4 border-t border-gray-200">
            <div className="flex gap-2">
              <input type="text" value={inputMessage} onChange={(e) => setInputMessage(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                placeholder="Ask a question about this entity..."
                className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500" />
              <button onClick={handleSendMessage} disabled={sending || !inputMessage.trim()}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50">
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

